import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, ChevronDown, Globe2, Loader2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthProvider } from '@auw/auth';
import { API_BASE_URL } from '@auw/api-client';
import { useFeatureFlags } from '@auw/feature-flags';
import { LOCALE_STORAGE_KEY, resolveAppLocale } from '@auw/i18n';
import { isDesktop, openExternalUrl } from '@auw/workspace-utils';
import { CookieBanner } from '@/components/common/CookieBanner';
import { rememberReturnPath, safeReturnPath } from '@/routes/authReturn';
import { getLandingCopy, type CaseCopy, type LandingCopy } from './landing/content';
import { HeroDemo } from './landing/LandingDemo';
import {
  AgentRoster, ApprovalMock, CapabilityCard, ConnectorWall, ControlsRow, DashboardMock, ErpMock, FeedMock, FlowMock, GroupChatMock,
  LiveMock, MemoryMock, MORE_ICONS, OnPremMock, OntologyMock, Reveal, SkillsMock, TeamMock, ToggleMock, TranslateMock, WikiMock,
} from './landing/LandingMocks';
import './landing/landing.css';

function GoogleLogo() {
  return <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path d="M47.532 24.553c0-1.632-.132-3.272-.414-4.877H24.48v9.242h12.963a11.089 11.089 0 0 1-4.804 7.28l-.044.291 6.972 5.404.483.048C43.96 38.14 47.532 31.87 47.532 24.553Z" fill="#4285F4" />
    <path d="M24.48 48c6.48 0 11.93-2.148 15.9-5.84l-7.41-5.742c-2.088 1.404-4.776 2.22-8.49 2.22-6.456 0-11.94-4.356-13.896-10.356l-.276.023-7.248 5.612-.095.264C7.236 42.588 15.24 48 24.48 48Z" fill="#34A853" />
    <path d="M10.584 28.282A14.768 14.768 0 0 1 9.78 24c0-1.5.252-2.952.78-4.282l-.014-.31-7.335-5.698-.24.114A24.022 24.022 0 0 0 .48 24c0 3.876.924 7.536 2.496 10.188l7.608-5.906Z" fill="#FBBC05" />
    <path d="M24.48 9.36c4.464 0 7.476 1.932 9.192 3.54l6.696-6.54C36.384 2.46 30.96 0 24.48 0 15.24 0 7.236 5.412 2.976 13.812L10.56 19.72c1.98-5.988 7.464-10.36 13.92-10.36Z" fill="#EB4335" />
  </svg>;
}

function Brand() {
  return <a className="landing-brand" href="#landing-top" aria-label="askyour.ai"><img src="/app_name.png" width="118" height="28" alt="" /></a>;
}

/** Scrolls to an in-page section without writing `#id` into the address bar. */
function scrollToSection(id: string): boolean {
  const target = id ? document.getElementById(id) : null;
  if (!target) return false;
  const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  // Move focus too, as a real anchor jump would, so keyboard and screen reader users follow along.
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  return true;
}

const LANGUAGES = [['ko', '한국어'], ['en', 'English'], ['ja', '日本語'], ['zh-CN', '简体中文'], ['zh-TW', '繁體中文']] as const;

function Heading({ id, eyebrow, title, sub, align = 'center' }: { id: string; eyebrow: string; title: [string, string]; sub?: string; align?: 'center' | 'start' }) {
  return <div className={`landing-heading is-${align}`}>
    <span className="landing-eyebrow">{eyebrow}</span>
    <h2 id={id}>{title[0]}<br />{title[1]}</h2>
    {sub && <p>{sub}</p>}
  </div>;
}

function MockCard({ lead, body, tone, children, delay = 0 }: { lead: string; body: string; tone: number; children: React.ReactNode; delay?: number }) {
  return <Reveal as="article" className="landing-card" style={{ '--delay': `${delay}ms` } as React.CSSProperties}>
    <div className={`landing-well is-tone-${tone}`} aria-hidden="true">{children}</div>
    <p className="landing-caption"><strong>{lead}</strong> {body}</p>
  </Reveal>;
}

/** A customer story: the built system on the app wallpaper, then who, what and the three outcomes. */
function CaseCard({ item, tone, delay = 0, children }: { item: CaseCopy; tone: number; delay?: number; children: React.ReactNode }) {
  const mark = item.company.match(/[A-Z](?=[^A-Z]*$)/)?.[0] ?? item.company.charAt(0);
  return <Reveal as="article" className="landing-card landing-case" style={{ '--delay': `${delay}ms` } as React.CSSProperties}>
    <div className={`landing-well is-tone-${tone}`} aria-hidden="true">{children}</div>
    <div className="landing-case-body">
      <p className="landing-case-label"><span aria-hidden="true">{mark}</span><strong>{item.company}</strong>{item.kind}</p>
      <h3>{item.title}</h3>
      <p>{item.body}</p>
      <ul>{item.points.map(point => <li key={point}><Check size={15} strokeWidth={2.4} />{point}</li>)}</ul>
    </div>
  </Reveal>;
}

function Sections({ copy, loginButton, error }: { copy: LandingCopy; loginButton: (variant?: 'hero' | 'nav') => React.ReactNode; error: boolean }) {
  const { integrations: integ, teams, everyday } = copy;
  return <div className="landing-column">
    <section className="landing-band landing-intro" aria-labelledby="intro-title">
      <span className="landing-eyebrow" id="intro-title">{copy.intro.eyebrow}</span>
      <div className="landing-intro-copy"><p><strong>{copy.intro.lead1}</strong>{copy.intro.body1}</p><p><strong>{copy.intro.lead2}</strong>{copy.intro.body2}</p></div>
    </section>

    <section className="landing-band" id="capabilities" aria-labelledby="capabilities-title">
      <Heading id="capabilities-title" eyebrow={copy.capabilities.eyebrow} title={copy.capabilities.title} sub={copy.capabilities.sub} />
      <div className="landing-cards is-3">{copy.capabilities.cards.map((card, i) => <CapabilityCard key={card.lead} copy={copy} index={i} />)}</div>
      <div className="landing-subband">
        <div className="landing-subhead"><h3>{copy.capabilities.rosterTitle}</h3><p>{copy.capabilities.rosterSub}</p></div>
        <AgentRoster copy={copy} />
      </div>
    </section>

    <section className="landing-band landing-docs" id="docs" aria-labelledby="docs-title">
      <div className="landing-split">
        <Heading id="docs-title" eyebrow={copy.docs.eyebrow} title={copy.docs.title} sub={copy.docs.sub} align="start" />
        <ul className="landing-points">{copy.docs.points.map(([lead, body]) => <li key={lead}><Check size={16} strokeWidth={2.4} /><p><strong>{lead}</strong> {body}</p></li>)}
          <li className="landing-formats" aria-label="DOCX, XLSX, PPTX, PDF">{['DOCX', 'XLSX', 'PPTX', 'PDF'].map(format => <span key={format} className={`is-${format.toLowerCase()}`}>{format}</span>)}</li>
        </ul>
      </div>
      <Reveal className="landing-stage"><WikiMock copy={copy} /></Reveal>
    </section>

    <section className="landing-band" id="integrations" aria-labelledby="integrations-title">
      <Heading id="integrations-title" eyebrow={integ.eyebrow} title={integ.title} sub={integ.sub} />
      <ConnectorWall />
      <div className="landing-cards is-3">
        <MockCard lead={integ.cards[0][0]} body={integ.cards[0][1]} tone={0}><FlowMock copy={copy} /></MockCard>
        <MockCard lead={integ.cards[1][0]} body={integ.cards[1][1]} tone={1} delay={70}><ApprovalMock copy={copy} /></MockCard>
        <MockCard lead={integ.cards[2][0]} body={integ.cards[2][1]} tone={2} delay={140}><ToggleMock copy={copy} /></MockCard>
      </div>
    </section>

    <section className="landing-band" id="teams" aria-labelledby="teams-title">
      <Heading id="teams-title" eyebrow={teams.eyebrow} title={teams.title} sub={teams.sub} align="start" />
      <div className="landing-cards is-2">
        <MockCard lead={teams.cards[0][0]} body={teams.cards[0][1]} tone={1}><TeamMock copy={copy} /></MockCard>
        <MockCard lead={teams.cards[1][0]} body={teams.cards[1][1]} tone={2} delay={70}><SkillsMock copy={copy} /></MockCard>
        <MockCard lead={teams.cards[2][0]} body={teams.cards[2][1]} tone={0}><GroupChatMock copy={copy} /></MockCard>
        <MockCard lead={teams.cards[3][0]} body={teams.cards[3][1]} tone={1} delay={70}><DashboardMock copy={copy} /></MockCard>
      </div>
      <ControlsRow copy={copy} />
    </section>

    <section className="landing-band is-muted" aria-labelledby="everyday-title">
      <Heading id="everyday-title" eyebrow={everyday.eyebrow} title={everyday.title} sub={everyday.sub} />
      <div className="landing-cards is-4">
        <MockCard lead={everyday.cards[0][0]} body={everyday.cards[0][1]} tone={3}><MemoryMock copy={copy} /></MockCard>
        <MockCard lead={everyday.cards[1][0]} body={everyday.cards[1][1]} tone={3} delay={70}><LiveMock copy={copy} /></MockCard>
        <MockCard lead={everyday.cards[2][0]} body={everyday.cards[2][1]} tone={3} delay={140}><TranslateMock copy={copy} /></MockCard>
        <MockCard lead={everyday.cards[3][0]} body={everyday.cards[3][1]} tone={3} delay={210}><FeedMock copy={copy} /></MockCard>
      </div>
      <ul className="landing-more">{everyday.more.map(([lead, body], i) => {
        const Icon = MORE_ICONS[i];
        return <li key={lead}><Icon size={17} strokeWidth={1.8} /><p><strong>{lead}</strong> {body}</p></li>;
      })}</ul>
    </section>

    <section className="landing-band" id="cases" aria-labelledby="cases-title">
      <Heading id="cases-title" eyebrow={copy.cases.eyebrow} title={copy.cases.title} sub={copy.cases.sub} align="start" />
      <div className="landing-cards is-3 landing-cases">
        <CaseCard item={copy.cases.items[0]} tone={0}><OnPremMock copy={copy} /></CaseCard>
        <CaseCard item={copy.cases.items[1]} tone={1} delay={70}><OntologyMock copy={copy} /></CaseCard>
        <CaseCard item={copy.cases.items[2]} tone={2} delay={140}><ErpMock copy={copy} /></CaseCard>
      </div>
    </section>

    <section className="landing-band landing-faq" id="faq" aria-labelledby="faq-title">
      <div><span className="landing-eyebrow">{copy.faq.eyebrow}</span><h2 id="faq-title">{copy.faq.title}</h2></div>
      <div className="landing-faq-list">{copy.faq.items.map(item => <details key={item.q}><summary><span>{item.q}</span><Plus size={18} /></summary><p>{item.a}</p></details>)}</div>
    </section>

    <section className="landing-band landing-closing-band" aria-labelledby="closing-title">
      <div className="landing-closing">
        <h2 id="closing-title">{copy.closing.title[0]}<br />{copy.closing.title[1]}</h2>
        <p>{copy.closing.sub}</p>
        <div className="landing-actions">{loginButton()}<a className="landing-secondary" href="/contact-sales">{copy.contact}<ArrowRight size={16} /></a></div>
        {error && <p className="landing-login-error" role="alert">{copy.loginError}</p>}
      </div>
    </section>
  </div>;
}

export const AuthPage: React.FC = () => {
  const { i18n } = useTranslation('auth');
  const flags = useFeatureFlags();
  const marketing = flags.isEnabled('auth.marketingLanding');
  const googleProvider = useAuthProvider('google_oauth');
  const copy = getLandingCopy(i18n.language);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const signingIn = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  const hero = useRef<HTMLElement>(null);
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (saved && saved !== i18n.language) void i18n.changeLanguage(resolveAppLocale(saved));
    } catch { /* Use the browser language when storage is unavailable. */ }
  }, [i18n]);
  useEffect(() => { document.documentElement.lang = i18n.language; }, [i18n.language]);
  // A `/#faq` link (on load or typed later) still lands on its section; the hash is then dropped from the URL.
  useEffect(() => {
    const jumpToHash = () => {
      let id = '';
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (!id || !scrollToSection(id)) return;
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    };
    jumpToHash();
    window.addEventListener('hashchange', jumpToHash);
    return () => window.removeEventListener('hashchange', jumpToHash);
  }, []);
  const handleSectionLink = (event: React.MouseEvent<HTMLElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element).closest?.('a[href^="#"]');
    if (link && scrollToSection(link.getAttribute('href')!.slice(1))) event.preventDefault();
  };
  const handleGoogleLogin = useCallback(async () => {
    if (signingIn.current || !googleProvider) return;
    signingIn.current = true; setPending(true); setError(false);
    // `/login?next=/doc-agent/…` returns there after the Google round trip (read in AuthCallbackPage).
    rememberReturnPath(safeReturnPath(new URLSearchParams(window.location.search).get('next')));
    try {
      if (isDesktop && googleProvider.getLoginUrl) {
        const webOrigin = API_BASE_URL.includes('api-dev') ? 'https://dev.askyour.ai' : 'https://askyour.ai';
        await openExternalUrl(googleProvider.getLoginUrl({ redirectUri: `${webOrigin}/auth/callback?desktop=1` }));
      } else {
        await googleProvider.startLogin({ redirectUri: `${window.location.origin}/auth/callback` });
      }
    } catch { setError(true); }
    finally { signingIn.current = false; setPending(false); }
  }, [googleProvider]);

  const loginButton = (variant: 'hero' | 'nav' = 'hero') => <button type="button" className={`landing-login is-${variant}`} onClick={() => void handleGoogleLogin()} disabled={!googleProvider || pending} aria-busy={pending} data-track="auth_google_login">
    {pending ? <Loader2 size={17} className="landing-spinner" /> : variant === 'hero' ? <span className="landing-google-mark"><GoogleLogo /></span> : null}
    <span>{pending ? copy.loading : variant === 'nav' ? copy.signIn : copy.login}</span>
  </button>;

  const languageSelect = <label className="landing-language"><Globe2 size={16} /><select aria-label={copy.language} value={resolveAppLocale(i18n.language)} onChange={event => {
    const locale = event.target.value; void i18n.changeLanguage(locale); try { localStorage.setItem(LOCALE_STORAGE_KEY, locale); } catch { /* Language changes still work when storage is disabled. */ }
  }}>{LANGUAGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><ChevronDown size={12} /></label>;

  const navLinks = marketing && <nav aria-label={copy.nav.menu}>
    <a href="#capabilities">{copy.nav.capabilities}</a><a href="#cases">{copy.nav.cases}</a><a href="/pricing">{copy.nav.pricing}</a>
  </nav>;

  const onScroll = () => {
    const threshold = (hero.current?.offsetHeight ?? 800) - 40;
    const next = (root.current?.scrollTop ?? 0) > threshold;
    setPastHero(previous => previous === next ? previous : next);
  };

  return <div className="auth-landing" ref={root} onScroll={onScroll} onClick={handleSectionLink}>
    <a className="landing-skip" href="#landing-main">{copy.skip}</a>
    {marketing && <header className={`landing-sticky ${pastHero ? 'is-visible' : ''}`} aria-hidden={!pastHero}>
      <div className="landing-sticky-inner"><Brand />{navLinks}<div className="landing-nav-actions">{loginButton('nav')}</div></div>
    </header>}
    <main id="landing-main">
      <section className={`landing-hero ${marketing ? '' : 'is-auth-only'}`} id="landing-top" ref={hero} aria-labelledby="landing-title">
        <div className="landing-hero-bg" aria-hidden="true" />
        <header className="landing-nav"><Brand />{navLinks}<div className="landing-nav-actions">{languageSelect}{loginButton('nav')}</div></header>
        <div className="landing-hero-copy">
          <h1 id="landing-title">{copy.hero.title}</h1>
          <div className="landing-hero-row">
            <p><strong>{copy.hero.lead}</strong> {copy.hero.sub}</p>
            <div className="landing-actions">{loginButton()}{marketing && <a className="landing-secondary" href="/contact-sales">{copy.contact}<ArrowRight size={16} /></a>}</div>
          </div>
          {error && <p className="landing-login-error" role="alert">{copy.loginError}</p>}
        </div>
        {marketing && <HeroDemo copy={copy} />}
      </section>
      {marketing && <Sections copy={copy} loginButton={loginButton} error={error} />}
    </main>
    <footer className="landing-footer">
      <div className="landing-footer-brand"><Brand /><p>{copy.footer.tagline}</p></div>
      {marketing && <div className="landing-footer-col"><strong>{copy.footer.product}</strong><a href="#capabilities">{copy.nav.capabilities}</a><a href="#cases">{copy.nav.cases}</a><a href="/pricing">{copy.nav.pricing}</a></div>}
      <div className="landing-footer-col"><strong>{copy.footer.company}</strong><a href="/contact-sales">{copy.contact}</a><a href="mailto:askyour.ai@reboott.ai">{copy.footer.email}</a></div>
      <div className="landing-footer-col"><strong>{copy.footer.legal}</strong><a href="/terms">{copy.footer.terms}</a><a href="/privacy">{copy.footer.privacy}</a></div>
      <small>© {new Date().getFullYear()} TEAMREBOOTT, Inc.</small>
    </footer>
    {flags.isEnabled('auth.cookieBanner') && <CookieBanner />}
  </div>;
};
